import { useState } from "react";
import { Paper, IconButton } from "@mui/material";
import ModeIcon from "@mui/icons-material/Mode";
import AddIcon from "@mui/icons-material/Add";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import {
  GenericAutocompleteModalModel,
  AutocompleteModel,
  AutocompleteModelForVehicleModels,
  AutocompleteModelForAddress,
  AutocompleteModelForFirmaAchizitie,
  AutocompleteModelForTipTVA,
} from "../../../Models/Models";
import {
  AutocompleteSchema,
  AutocompleteSchemaForAddress,
  AutocompleteSchemaForFirmaAchizitie,
  AutocompleteSchemaForTipTVA,
} from "../../../Validators/vehicleValidators";
import { Vocabulary } from "../../../Utils/Vocabulary";
import { isMobile } from "../../../Utils/utils";
import GenericAutocompleteModal from "../../../Modal/GenericAutocompleteModal";
import User from "../../Users/User";
import CompanyModal from "../../Settings/CompanyModal";

// Column definitions for modal tables
const columns = [
  { name: "id", label: "ID", options: { display: false } },
  { name: "nume", label: Vocabulary.name },
];

const columnsBrand = [
  { name: "id", label: "ID", options: { display: false } },
  { name: "nume", label: Vocabulary.name },
  { name: "logo", label: "Logo", options: { display: false } },
];

const columnsModel = [
  { name: "id", label: "ID", options: { display: false } },
  { name: "nume", label: Vocabulary.name },
  { name: "brand_id", label: "Brand ID", options: { display: false } },
];

const columnsLocatieAdministrativa = [
  { name: "id", label: "ID", options: { display: false } },
  { name: "nume", label: "Oras" },
  { name: "judet", label: "Judet" },
  { name: "adresa", label: "Adresa" },
  { name: "telefon", label: "Telefon" },
];

const columnsFirmaAchizitie = [
  { name: "id", label: "ID", options: { display: false } },
  { name: "nume_firma", label: "Nume Firma" },
];

const columnsTipTVA = [
  { name: "id", label: "ID", options: { display: false } },
  { name: "nume", label: Vocabulary.name },
  { name: "valoare", label: "Valoare" },
];

const columnsUsers = [
  { name: "id", label: "ID", options: { display: false } },
  { name: "name", label: Vocabulary.name },
];

const columnsFurnizor = [
  { name: "id", label: "ID", options: { display: false } },
  { name: "nume", label: Vocabulary.name },
];

interface UseAutocompleteModalOptions {
  brandId?: number | string;
}

export const useAutocompleteModal = (
  options: UseAutocompleteModalOptions = {},
) => {
  const { brandId } = options;
  const [genericAutocompleteModel, setGenericAutocompleteModel] = useState<any>(
    new GenericAutocompleteModalModel(),
  );
  const [openModalUser, setOpenModalUser] = useState(false);
  const [openModalCompany, setOpenModalCompany] = useState(false);

  // Get columns based on the field ID
  const getAutocompleteColumns = (id: string) => {
    switch (id) {
      case "brand_r":
        return columnsBrand;
      case "model_r":
        return columnsModel;
      case "locatie_administrativa_r":
        return columnsLocatieAdministrativa;
      case "firma_achizitie_r":
        return columnsFirmaAchizitie;
      case "tip_tva_r":
        return columnsTipTVA;
      case "user_achizitie_r":
      case "user_logistica_r":
      case "user_intermediere":
        return columnsUsers;
      case "furnizor_r":
        return columnsFurnizor;
      case "tip_achizitie_r":
        return columns;
      default:
        return columns;
    }
  };

  // Get validation schema based on the field ID
  const getAutocompleteValidationSchema = (id: string) => {
    switch (id) {
      case "locatie_administrativa_r":
        return AutocompleteSchemaForAddress;
      case "firma_achizitie_r":
        return AutocompleteSchemaForFirmaAchizitie;
      case "tip_tva_r":
        return AutocompleteSchemaForTipTVA;
      default:
        return AutocompleteSchema;
    }
  };

  // Get the model for form fields based on the field ID
  const getAutocompleteModel = (id: string) => {
    switch (id) {
      case "model_r":
        return {
          ...new AutocompleteModelForVehicleModels(),
          brand_id: brandId,
        };
      case "locatie_administrativa_r":
        return new AutocompleteModelForAddress();
      case "firma_achizitie_r":
        return new AutocompleteModelForFirmaAchizitie();
      case "tip_tva_r":
        return new AutocompleteModelForTipTVA();
      default:
        return new AutocompleteModel();
    }
  };

  // Get hidden fields array based on the field ID
  const getHiddenFieldsArray = (id: string) => {
    switch (id) {
      case "brand_r":
        return ["id", "logo"];
      case "model_r":
        return ["id", "brand_id"];
      default:
        return ["id"];
    }
  };

  // Helper function to open the modal for editing/adding dropdown options
  const handleClickEditDrop = (
    data: any,
    title: string,
    id: string,
    addOnly = false,
  ) => {
    setGenericAutocompleteModel({
      ...genericAutocompleteModel,
      open: true,
      title: title,
      data: data,
      id: id,
      columns: getAutocompleteColumns(id),
      validationSchema: getAutocompleteValidationSchema(id),
      hiddenFieldsArray: getHiddenFieldsArray(id),
      modelForFields: getAutocompleteModel(id),
      addOnly: addOnly,
    });
  };

  // Close the modal
  const closeModal = () => {
    setGenericAutocompleteModel({
      ...genericAutocompleteModel,
      open: false,
    });
  };

  // DropDownButton component - adds edit/add button to autocomplete dropdown
  const DropDownButton = ({ children, ...other }: any) => (
    <Paper {...other}>
      <IconButton
        onMouseDown={(event) => {
          event.preventDefault();
          const ownerState =
            children[1] === "null" || children[1] === null
              ? children[2]?.props?.ownerState
              : children[1]?.props?.ownerState;
          if (ownerState) {
            handleClickEditDrop(
              ownerState.options,
              ownerState.label,
              ownerState.id,
            );
          }
        }}
        style={{
          position: "absolute",
          top: -40,
          right: isMobile() ? 0 : -36,
          margin: 5,
          display: "flex",
          justifyContent: "end",
          backgroundColor: "#FF6633",
          color: "#fff",
          borderRadius: 7,
          zIndex: 100,
        }}
        size="small"
        aria-label="editDropdown"
      >
        <ModeIcon fontSize="inherit" />
      </IconButton>
      {children}
    </Paper>
  );

  // Modal component to render
  const AutocompleteModalComponent = () => (
    <GenericAutocompleteModal
      open={genericAutocompleteModel.open || false}
      onClose={closeModal}
      title={genericAutocompleteModel.title}
      data={genericAutocompleteModel.data}
      id={genericAutocompleteModel.id}
      columns={genericAutocompleteModel.columns}
      validationSchema={genericAutocompleteModel.validationSchema}
      hiddenFieldsArray={genericAutocompleteModel.hiddenFieldsArray}
      modelForFields={genericAutocompleteModel.modelForFields}
      addOnly={genericAutocompleteModel.addOnly || false}
      onEdit={(_: any, tableMeta: any) => {
        const rowData = genericAutocompleteModel.data?.[tableMeta.rowIndex];
        if (rowData) {
          setGenericAutocompleteModel({
            ...genericAutocompleteModel,
            modelForFields: rowData,
          });
        }
      }}
    >
      <></>
    </GenericAutocompleteModal>
  );

  const AddButton = ({ children, ...other }: any) => (
    <Paper {...other}>
      <IconButton
        onMouseDown={(event) => {
          event.preventDefault();

          const ownerState =
            children[1] === "null" || children[1] === null
              ? children[2]?.props?.ownerState
              : children[1]?.props?.ownerState;

          if (ownerState) {
            handleClickEditDrop(
              ownerState.options,
              ownerState.label,
              ownerState.id,
              true, // 👈 addOnly mode
            );
          }
        }}
        style={{
          position: "absolute",
          top: -40,
          right: isMobile() ? 0 : -36,
          margin: 5,
          display: "flex",
          justifyContent: "end",
          backgroundColor: "#FF6633",
          color: "#fff",
          borderRadius: 7,
          zIndex: 100,
        }}
        size="small"
        aria-label="addOption"
      >
        <AddIcon fontSize="inherit" />
      </IconButton>
      {children}
    </Paper>
  );

  // UserAddButton - opens User modal directly (like EditButton in VehicleMainInfo.js)
  const UserAddButton = ({ children, ...other }: any) => (
    <Paper {...other}>
      <IconButton
        onMouseDown={(event) => {
          event.preventDefault();
          setOpenModalUser(true);
        }}
        style={{
          position: "absolute",
          top: -40,
          right: isMobile() ? 0 : -36,
          margin: 5,
          display: "flex",
          justifyContent: "end",
          backgroundColor: "#FF6633",
          color: "#fff",
          borderRadius: 7,
          zIndex: 100,
        }}
        size="small"
        aria-label="addUser"
      >
        <AddCircleIcon fontSize="inherit" />
      </IconButton>
      {children}
    </Paper>
  );

  // CompanyAddButton - opens CompanyModal directly (like EditButtonCompany in VehicleMainInfo.js)
  const CompanyAddButton = ({ children, ...other }: any) => (
    <Paper {...other}>
      <IconButton
        onMouseDown={(event) => {
          event.preventDefault();
          setOpenModalCompany(true);
        }}
        style={{
          position: "absolute",
          top: -40,
          right: isMobile() ? 0 : -36,
          margin: 5,
          display: "flex",
          justifyContent: "end",
          backgroundColor: "#FF6633",
          color: "#fff",
          borderRadius: 7,
          zIndex: 100,
        }}
        size="small"
        aria-label="addCompany"
      >
        <AddCircleIcon fontSize="inherit" />
      </IconButton>
      {children}
    </Paper>
  );

  // User Modal Component
  const UserModalComponent = () => (
    <User
      open={openModalUser}
      onClose={() => setOpenModalUser(false)}
      title={Vocabulary.addNewUser}
      visibilityUser={false}
      editedUser={null}
    />
  );

  // Company Modal Component
  const CompanyModalComponent = () => (
    <CompanyModal
      open={openModalCompany}
      title={Vocabulary.addNewCompany}
      onClose={() => setOpenModalCompany(false)}
      editedShipping={null}
    />
  );

  return {
    DropDownButton,
    AutocompleteModalComponent,
    AddButton,
    UserAddButton,
    CompanyAddButton,
    UserModalComponent,
    CompanyModalComponent,
    handleClickEditDrop,
    closeModal,
    genericAutocompleteModel,
    openModalUser,
    setOpenModalUser,
    openModalCompany,
    setOpenModalCompany,
  };
};

export default useAutocompleteModal;
